import { useState, useEffect } from "react";
import { LogOut, UserPlus, Users, FolderOpen, Settings, Clock, Inbox, Receipt, FileText, BarChart3, History, DollarSign, ClipboardCheck, ShieldCheck, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster } from "sonner";
import axios from "axios";
import PetrocontrolLogo from "@/components/PetrocontrolLogo";
import NotificationBell from "@/components/NotificationBell";

// Import tab components
import ProspectosTab from "./admin/ProspectosTab";
import CotizacionesTab from "./admin/CotizacionesTab";
import ClientesTab from "./admin/ClientesTab";
import PreTramitesTab from "./admin/PreTramitesTab";
import TramitesTab from "./admin/TramitesTab";
import ConfigTab from "./admin/ConfigTab";
import DashboardTab from "./admin/DashboardTab";
import AuditTab from "./admin/AuditTab";
import ComisionesTab from "./admin/ComisionesTab";
import DiagnosticosTab from "./admin/DiagnosticosTab";
import ValidacionTab from "./admin/ValidacionTab";
import ContratoTab from "./admin/ContratoTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

function StatsCards({ stats }) {
  const cards = [
    { label: "Prospectos", value: stats?.diagnostic_requests?.total || 0, icon: UserPlus, color: "bg-amber-600" },
    { label: "Clientes", value: stats?.clients?.total || 0, icon: Users, color: "bg-[#1a5c4a]" },
    { label: "Tramites", value: stats?.tramites?.total || 0, icon: FolderOpen, color: "bg-purple-600" },
    { label: "Pendientes", value: stats?.diagnostic_requests?.pending || 0, icon: Clock, color: "bg-red-500" },
  ];
  
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card key={c.label} className="border-none shadow-sm" data-testid={`stat-${c.label.toLowerCase()}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${c.color} flex items-center justify-center`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a1a2e]">{c.value}</p>
                  <p className="text-xs text-gray-600">{c.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState(() => {
    // Restaurar tab desde localStorage
    return localStorage.getItem('admin_active_tab') || "prospectos";
  });
  const [stats, setStats] = useState(null);

  // Guardar tab activo en localStorage
  useEffect(() => {
    localStorage.setItem('admin_active_tab', tab);
  }, [tab]);

  useEffect(() => { 
    axios.get(`${API}/admin/stats`, axCfg)
      .then(({ data }) => setStats(data))
      .catch(() => {}); 
  }, [tab]);
  
  // Auto-refresh stats cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/admin/stats`, axCfg)
        .then(({ data }) => setStats(data))
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { id: "prospectos", label: "Prospectos", icon: UserPlus },
    { id: "diagnosticos", label: "Diagnósticos", icon: ClipboardCheck },
    { id: "cotizaciones", label: "Cotizaciones", icon: FileText },
    { id: "validacion", label: "Validación", icon: ShieldCheck },
    { id: "contratos", label: "Contratos", icon: FileSignature },
    { id: "pretramites", label: "Pre-Trámite", icon: Receipt },
    { id: "clientes", label: "Clientes", icon: Users },
    { id: "tramites", label: "Trámites", icon: FolderOpen },
    { id: "comisiones", label: "Comisiones", icon: DollarSign },
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "auditoria", label: "Auditoría", icon: History },
    { id: "configuracion", label: "Config", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#f5f0e8]">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-[#1a1a2e] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <PetrocontrolLogo className="h-8" />
            <span className="text-white/50 text-xs ml-2 hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell onNavigateToProspectos={() => setTab("prospectos")} />
            <Button 
              variant="ghost" 
              onClick={onLogout} 
              className="text-white/60 hover:text-white text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats cards */}
        {stats && <StatsCards stats={stats} />}
        
        {/* Tab navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button 
                key={t.id} 
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  tab === t.id 
                    ? "bg-[#1a5c4a] text-white" 
                    : "bg-white text-gray-700 border border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="h-4 w-4" />{t.label}
              </button>
            );
          })}
        </div>
        
        {/* Tab content */}
        {tab === "prospectos" && <ProspectosTab />}
        {tab === "diagnosticos" && <DiagnosticosTab />}
        {tab === "cotizaciones" && <CotizacionesTab />}
        {tab === "validacion" && <ValidacionTab />}
        {tab === "contratos" && <ContratoTab />}
        {tab === "pretramites" && <PreTramitesTab />}
        {tab === "clientes" && <ClientesTab />}
        {tab === "tramites" && <TramitesTab />}
        {tab === "comisiones" && <ComisionesTab />}
        {tab === "dashboard" && <DashboardTab />}
        {tab === "auditoria" && <AuditTab />}
        {tab === "configuracion" && <ConfigTab />}
      </div>
    </div>
  );
}
